import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { getPoetById, getFullPoemId } from '../../services/poetryService';
import { fetchWikipediaBio, WikiNotFoundError, cyrillicKeyToLatin } from '../../services/wikipediaService';
import { usePageNavigation } from '../../hooks/useNavigation';
import { getWiki } from '../../types/poetry';

interface BiographyReaderProps {
  onZenModeChange: (isZen: boolean) => void;
}

const BiographyReader: React.FC<BiographyReaderProps> = ({ onZenModeChange }) => {
  const { poetId } = useParams<{ poetId: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wikiUrl, setWikiUrl] = useState<string | null>(null);
  const [isZenMode, setIsZenMode] = useState(false);

  const result = poetId ? getPoetById(poetId) : null;
  const poet = result?.poet ?? null;
  const catalog = result?.catalog ?? null;

  // Next is the first poem
  const nextPoem = poet && poet.poems.length > 0
    ? { id: getFullPoemId(poet.id, poet.poems[0].id), name: poet.poems[0].name }
    : null;

  // Keyboard and swipe navigation (no prev for biography)
  const { onTouchStart, onTouchEnd } = usePageNavigation(null, nextPoem);

  useEffect(() => {
    const primaryKey = getWiki(result?.poet);
    if (!primaryKey) {
      setError('Биографија није доступна за овог песника.');
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadBio = async () => {
      setLoading(true);
      setError(null);
      setContent('');
      setWikiUrl(null);

      // Step 1: try primary key (Cyrillic name-derived or explicit wiki attribute)
      try {
        const html = await fetchWikipediaBio(primaryKey);
        if (isMounted) {
          setContent(html);
          setWikiUrl(primaryKey);
          setLoading(false);
        }
        return;
      } catch (e) {
        if (!(e instanceof WikiNotFoundError)) {
          if (isMounted) {
            setError('Није могуће учитати биографију. Покушајте поново.');
            setLoading(false);
          }
          return;
        }
      }

      // Step 2: try Latin transliteration fallback
      const latinKey = cyrillicKeyToLatin(primaryKey);
      if (latinKey !== primaryKey) {
        try {
          const html = await fetchWikipediaBio(latinKey);
          if (isMounted) {
            setContent(html);
            setWikiUrl(latinKey);
            setLoading(false);
          }
          return;
        } catch (e) {
          // fall through to failure message
        }
      }

      // Both attempts failed
      if (isMounted) {
        setError('Биографија није доступна за овог песника.');
        setLoading(false);
      }
    };

    loadBio();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poetId]);

  const toggleZenMode = useCallback(() => {
    setIsZenMode(prev => {
      const newValue = !prev;
      onZenModeChange(newValue);
      return newValue;
    });
  }, [onZenModeChange]);

  if (!poetId || !result || !poet || !catalog) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={`biography-reader ${isZenMode ? 'biography-reader--zen-mode' : ''}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="biography-reader__title-bar">
        <div className="biography-reader__nav biography-reader__nav--placeholder" />

        <div className="biography-reader__title-content">
          <div className="biography-reader__label">О песнику</div>
          <div className="biography-reader__poet-name">{poet.name}</div>
        </div>

        {nextPoem ? (
          <Link to={`/poem/${nextPoem.id}`} className="biography-reader__nav biography-reader__nav--next" aria-label={nextPoem.name}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </Link>
        ) : (
          <div className="biography-reader__nav biography-reader__nav--placeholder" />
        )}
      </div>

      {loading && (
        <div className="biography-reader__loading">Учитавање...</div>
      )}

      {error && (
        <div className="biography-reader__error">{error}</div>
      )}

      {!loading && !error && (
        <div
          className="biography-reader__content"
          dangerouslySetInnerHTML={{ __html: content }}
          onClick={toggleZenMode}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleZenMode()}
          aria-label={isZenMode ? 'Изађи из режима читања' : 'Уђи у режим читања'}
        />
      )}

      {!loading && !error && wikiUrl && (
        <div className="biography-reader__source">
          Садржај преузет са <a href={`https://sr.wikipedia.org/wiki/${encodeURIComponent(wikiUrl)}`} target="_blank" rel="noopener noreferrer">Википедије</a> под лиценцом <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer">CC BY-SA 4.0</a>
        </div>
      )}
    </div>
  );
};

export default BiographyReader;

